import * as THREE from "three/webgpu";
import { Suspense } from "react";
import { Canvas, extend } from "@react-three/fiber";
import { BakeShadows, Environment, Float, Html } from "@react-three/drei";
import Ocean from "./Ocean";
import Scoutglb from "./Scoutglb";
import IntroOverlay from "./IntroOverlay/IntroOverlay";
import AnimateCamera from "./AnimateCamera";
import CloudsComposition from "./CloudsComposition";
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import { Radar } from "./Radar";
import Birds from "./Birds";
import Config from "./Config/Config";
import PresetsPanel from "./PresetsPanel/PresetsPanel";
import HullColorsPanel from "./HullColorsPanel/HullColorsPanel";
import AccentColorsPanel from "./AccentColorsPanel/AccentColorsPanel";
import SeatsColorsPanel from "./SeatsColorsPanel/SeatsColorsPanel";
import EnvironmentPanel from "./EnvironmentPanel/EnvironmentPanel";
import DeckPanel from "./DeckColorsPanel/DeckColorsPanel";
import { useSnapshot } from "valtio";
import { state } from "./store";
import Effects from "./Effects";
import LoadingScreen from "./LoadingScreen/LoadingScreen";
import { calculateAdaptiveDPR } from "./utils/dprHelper";
import { useEffect, useState } from "react";
import BodyColorsPanel from "./BodyColorsPanel/BodyColorsPanel";

export default function App(props) {
  const { qualitySettings, viewType, environmentHdriUrl, environmentBackgroundIntensity } = useSnapshot(state);

  const [adaptiveDPR, setAdaptiveDPR] = useState(() => calculateAdaptiveDPR());

  // Update DPR on window resize
  useEffect(() => {
    const handleResize = () => {
      setAdaptiveDPR(calculateAdaptiveDPR());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Canvas
        gl={async (props) => {
          extend(THREE);
          props.antialias = false;
          // Disable MSAA at construction to avoid depth resolve mismatch in three@1.181.1
          // props.samples = 0;
          props.antialias = false;
          props.alpha = false;
          // props.toneMapping = THREE.NoToneMapping;
          const renderer = new THREE.WebGPURenderer(props);

          renderer.powerPreference = "high-performance";

          renderer.toneMapping = THREE.CineonToneMapping;
          renderer.toneMappingExposure = 0.7;

          await renderer.init();

          return renderer;
        }}
        shadows
        dpr={adaptiveDPR}
        camera={{ position: [-1, 160, -1], fov: 85, near: 2, far: 1000 }}
        {...props}
      >
        <color attach="background" args={["#2a6f97"]} />
        <ambientLight intensity={1.5} />
        <spotLight
          castShadow
          position={[130, 50, 80]}
          decay={0.1}
          angle={0.5}
          color={"orange"}
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={80}
          shadow-camera-far={300}
          shadow-camera-left={-90}
          shadow-camera-right={90}
          shadow-camera-top={90}
          shadow-camera-bottom={-100}
          penumbra={1}
          intensity={8}
          shadow-bias={0.0001}
          shadow-normalBias={0.9}
        />

        {/* <LensFlareAdvanced position={[350, 80, 250]} /> */}

        <AnimateCamera enabled={true} />

        <Suspense fallback={<LoaderElement />}>
          <Environment files={environmentHdriUrl || "https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/2k/kloppenheim_06_puresky_2k.hdr"} background={true} backgroundIntensity={environmentBackgroundIntensity || 0.3} environmentIntensity={0.65} resolution={2048} />
        </Suspense>

        <BakeShadows />
        <CloudsComposition />

        <Float floatIntensity={1.5} floatingRange={[-0.8, 0.1]} speed={4} rotationIntensity={0.1} enabled={viewType !== "cabin"}>
          <Scoutglb scale={5.1} position={[55, -5, 0]} />
          <Radar scale={0.2} position={[55, -5, 0]} />
        </Float>
        {/* Ocean Component */}
        <Ocean position={[0, -5, 0]} size={[1500, 1500]} sunDirection={[10.5, 2.0, 10]} sunColor={"#54a9e3"} waterColor={"#003f5e"} distortionScale={15} />

        {qualitySettings !== "performance" && <Effects />}

        <Birds />
      </Canvas>

      <IntroOverlay />
      <Header />
      <Footer />
      <Config />
      <PresetsPanel />
      <HullColorsPanel />
      <EnvironmentPanel />
      <DeckPanel />
      <AccentColorsPanel />
      <SeatsColorsPanel />
      <BodyColorsPanel />
      <LoadingScreen />
    </>
  );
}

function LoaderElement() {
  const { finishedMainLoading } = useSnapshot(state);
  if (!finishedMainLoading) return null;
  return (
    <Html wrapperClass="loader-element" calculatePosition={() => [0, 0, 0]}>
      <div className="loader-element-text">
        <span className="loader-element-spinner"></span>Loading Environment...
      </div>
    </Html>
  );
}
